/**
 * ThemePreviewContent - Displays theme preview with all visual elements
 */

interface ThemePreviewContentProps {
  theme: {
    colors: Record<string, string>;
    typography: Record<string, string | number>;
    spacing: Record<string, string>;
    brand: Record<string, string>;
    shadows: Record<string, string>;
    layout: Record<string, string>;
    metadata: {
      version: string;
      deviceType: 'desktop' | 'mobile';
      updatedAt: string;
    };
  };
  deviceType: 'desktop' | 'mobile';
}

export function ThemePreviewContent({ theme }: ThemePreviewContentProps) {
  const colors = theme.colors;

  return (
    <div
      className="theme-preview-content"
      style={{
        '--color-primary': colors.primary || '#C4A484',
        '--color-primary-hover': colors.primaryHover || '#E8D5C4',
        '--color-bg-primary': colors.bgPrimary || '#FAF8F5',
        '--color-bg-secondary': colors.bgSecondary || '#F5F1EA',
        '--color-text-primary': colors.textPrimary || '#3E3328',
        '--color-text-secondary': colors.textSecondary || '#6D5F52',
        '--color-border': colors.borderPrimary || '#E8E2D8',
      } as React.CSSProperties}
    >
      {/* Color Palette Preview */}
      <section className="preview-section">
        <h2>色彩樣本</h2>
        <div className="color-grid">
          <ColorSwatch label="Primary" color={colors.primary} />
          <ColorSwatch label="Primary Hover" color={colors.primaryHover} />
          <ColorSwatch label="Background Primary" color={colors.bgPrimary} />
          <ColorSwatch label="Background Secondary" color={colors.bgSecondary} />
          <ColorSwatch label="Text Primary" color={colors.textPrimary} />
          <ColorSwatch label="Text Secondary" color={colors.textSecondary} />
        </div>
      </section>

      {/* Typography Preview */}
      <section className="preview-section">
        <h2>字體樣式</h2>
        <div className="typography-preview">
          <div className="typography-item">
            <h3 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
              標題文字 Heading
            </h3>
            <p style={{ fontSize: '1rem', margin: 0, color: 'var(--color-text-secondary)' }}>
              這是標題的文字範例
            </p>
          </div>
          <div className="typography-item">
            <p style={{ fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
              這是正文文字的範例。The quick brown fox jumps over the lazy dog.
            </p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: 'var(--color-text-secondary)' }}>
              這是較小的文字範例
            </p>
          </div>
        </div>
      </section>

      {/* Component Preview */}
      <section className="preview-section">
        <h2>組件預覽</h2>
        <div className="component-preview">
          {/* Buttons */}
          <div className="component-group">
            <h3>按鈕</h3>
            <div className="button-group">
              <button className="btn-primary">主要按鈕</button>
              <button className="btn-secondary">次要按鈕</button>
            </div>
          </div>

          {/* Cards */}
          <div className="component-group">
            <h3>卡片</h3>
            <div className="card-preview">
              <div className="card">
                <h4>卡片標題</h4>
                <p>這是卡片的內容文字，展示主題的陰影和背景效果。</p>
              </div>
            </div>
          </div>

          {/* Form Elements */}
          <div className="component-group">
            <h3>表單元素</h3>
            <div className="form-preview">
              <input type="text" placeholder="輸入框範例" />
              <select>
                <option>下拉選單範例</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .theme-preview-content {
          --color-primary: ${colors.primary};
          --color-primary-hover: ${colors.primaryHover};
          --color-bg-primary: ${colors.bgPrimary};
          --color-bg-secondary: ${colors.bgSecondary};
          --color-text-primary: ${colors.textPrimary};
          --color-text-secondary: ${colors.textSecondary};
          --color-border: ${colors.borderPrimary};
        }
        .preview-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .preview-section h2 {
          margin: 0 0 1rem 0;
          font-size: 1.25rem;
          color: #333;
        }
        .color-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
        }
        .color-swatch {
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .color-swatch-box {
          height: 80px;
        }
        .color-swatch-label {
          padding: 0.5rem;
          font-size: 0.75rem;
          color: #666;
          background: white;
          text-align: center;
        }
        .typography-preview {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .typography-item {
          padding: 1rem;
          background: var(--color-bg-secondary);
          border-radius: 8px;
        }
        .component-preview {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        .component-group h3 {
          margin: 0 0 1rem 0;
          font-size: 1rem;
          color: #666;
        }
        .button-group {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .btn-primary, .btn-secondary {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-size: 0.875rem;
          cursor: pointer;
          border: none;
        }
        .btn-primary {
          background: var(--color-primary);
          color: white;
        }
        .btn-secondary {
          background: var(--color-bg-secondary);
          color: var(--color-text-primary);
          border: 1px solid var(--color-border);
        }
        .card-preview {
          padding: 1rem;
          background: var(--color-bg-secondary);
          border-radius: 8px;
        }
        .card {
          background: white;
          border-radius: 8px;
          padding: 1.25rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .card h4 {
          margin: 0 0 0.5rem 0;
          color: var(--color-text-primary);
        }
        .card p {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: 0.875rem;
        }
        .form-preview {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .form-preview input,
        .form-preview select {
          padding: 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
        }
        @media (max-width: 768px) {
          .color-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
}

interface ColorSwatchProps {
  label: string;
  color: string;
}

function ColorSwatch({ label, color }: ColorSwatchProps) {
  return (
    <div className="color-swatch">
      <div
        className="color-swatch-box"
        style={{ backgroundColor: color }}
      />
      <div className="color-swatch-label">
        <div>{label}</div>
        <div style={{ fontSize: '0.625rem', opacity: 0.7 }}>{color}</div>
      </div>
    </div>
  );
}
